import { h, defineComponent, ref, watch } from 'vue';
import { d, t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import {
  box,
  indexView,
  level,
  loader,
  pageTitle,
  pagination,
  sortableTextColumn,
  tag,
  textColumn,
} from '@/ui/components';
import { list as getNotificationList, markAllAsRead } from '@/services/api/resources/notification';
import { emptyList } from '@/services/api';
import { NotificationList, NotificationRecord } from '@/entities/notification';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { createdAtFilter, notificationUnreadFilter, searchFilter } from '@/ui/components/filters/filters-horizontal';
import { SortDirection } from '@/ui/types';
import { useNotificationCheck } from '@/ui/pages/notification/useNotificationCheck';
import { renderResourceEmptyPage } from '@/ui/pages/ResourceEmptyPage';
import { filterFormHorizontal } from '@/ui/components/Columns';
import { markAsReadButton } from '@/ui/components/Button';

export const NotificationIndexPage = defineComponent({
  name: 'NotificationIndexPage',
  setup() {
    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);

    const search = qs.string('q', '');
    const start = qs.nullableDate('from', null);
    const end = qs.nullableDate('to', null);
    const unread = qs.boolean('status', false);

    // get list task
    const list = ref<NotificationList>(emptyList());
    const getList = useTask(
      () => getNotificationList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        from: start.value,
        to: end.value,
        status: unread.value,
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );

    const { checkLoading, checkResult, performCheck, selectedResources, onCheckedRowsUpdate } = useNotificationCheck({
      getList,
      bulkUpdateFn: markAllAsRead,
    });

    watch([
      page,
      perPage,
      start,
      end,
      search,
      unread,
      sort,
      dir,
    ], getList.run);

    return function render() {
      return loader('div', performCheck, [
        level([pageTitle(t('common.notifications'))]),
        checkResult.value ? indexView(
          [
            filterFormHorizontal(
              [
                markAsReadButton({ resources: selectedResources, disabled: checkLoading }),
                createdAtFilter(start, end, resetPagination),
                searchFilter(search, resetPagination),
                notificationUnreadFilter(unread, resetPagination),
              ],
            ),
          ],
          [
            box([
              loader('div', getList, [
                h(ResourceTable, {
                  props: {
                    editRoute: 'notificationDetail',
                    list: list.value.data,
                    titleColumnLabel: t('common.subject'),
                    sort: sort.value,
                    dir: dir.value,
                    selectedRecords: selectedResources.value,
                    isRowCheckable: () => !checkLoading.value,
                    checkable: true,
                  },
                  scopedSlots: {
                    title: ({ row: notification }: { row: NotificationRecord }) => notification.data.subject,
                    default: () => [
                      sortableTextColumn({
                        field: 'createdAt',
                        label: t('common.created-at'),
                      }, {
                        default: ({ row: notification }: { row: NotificationRecord }) =>
                          d(notification.data.createdAt, 'shortDateTime'),
                      }),
                      textColumn({
                        field: 'status',
                        label: t('common.status'),
                      }, {
                        default: ({ row: notification }: { row: NotificationRecord }) =>
                          [notification.data.readAt === null
                            ? tag('error', t('common.unread'))
                            : tag('neutral', t('common.read'))],
                      }),
                    ],
                  },
                  on: {
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      resetPagination();
                      sort.value = field;
                      dir.value = direction;
                    }),
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : renderResourceEmptyPage(t('notifications.empty'), '/images/empty-notifications.svg'),
      ], false);
    };
  },
});
